<template>
  <div>
    <b-modal
      id="change-enrollment-grade-modal"
      ref="change-enrollment-grade-modal"
      title="修改課程"
      no-close-on-backdrop
      size="sm"
    >
      <v-select
        v-model="selected_enrollment.grade_id"
        style="width:100%"
        :options="gradeOptions"
        label="label"
        :clearable="false"
        :append-to-body="true"
        class="center-select"
        :reduce="grade => grade.grade_id"
      />

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">取消</b-button>
        <b-button size="sm" variant="primary" @click="submit_change_enrollment_grade('')">
          確定
        </b-button>
        <b-button size="sm" variant="warning" @click="submit_change_enrollment_grade('all')">
          確定（全部往後）
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="change-todo-modal"
      ref="change-todo-modal"
      title="修改畫題"
      no-close-on-backdrop
      size="sm"
    >
      <b-form-input
        v-model="selected_enrollment.todo_content"
        type="text"
      />

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="outline-secondary" @click="cancel()">取消</b-button>
        <b-button size="sm" variant="primary" @click="change_todo_content()">確定</b-button>
      </template>
    </b-modal>

    <change-enrollment-modal
      ref="changeEnrollmentModal"
      modal-id="change-date-modal-schedule"
      :teacher-options="staffOptions"
      :user-id="selected_enrollment.user_id"
      @changed="get_data"
    />

    <b-overlay :show="loading" rounded="sm">
      <b-card no-body>
        <!-- Filter bar -->
        <div class="p-1">
          <b-row class="align-items-center">
            <b-col cols="12" md="2" class="mb-1 mb-md-0">
              <flat-pickr
                v-model="rangeDate"
                class="form-control"
                :config="{ mode: 'single', dateFormat: 'Y-m-d' }"
              />
            </b-col>

            <b-col cols="12" md="4" class="d-flex align-items-center mb-1 mb-md-0">
              <v-select
                v-model="staff_id"
                class="center-select mr-50 flex-grow-1"
                :options="scheduleStaffOptions"
                label="label"
                :clearable="false"
                :append-to-body="true"
                :reduce="staff => staff.value"
                @input="get_data()"
              />

              <v-select
                v-model="selected_start"
                class="center-select flex-grow-1"
                :options="scheduleStartOptions"
                label="label"
                :clearable="false"
                :append-to-body="true"
                :reduce="start => start.value"
                @input="get_data()"
              />
            </b-col>

            <b-col cols="12" md="5" class="d-flex align-items-center mb-1 mb-md-0">
              <b-badge
                variant="light-primary"
                class="mr-1 action-badge"
                @click="selectAll"
              >
                全選
              </b-badge>

              <template v-if="selectedEnrollmentIds.length">
                <span class="mr-1">已選 {{ selectedEnrollmentIds.length }} 筆</span>

                <b-badge
                  variant="success"
                  class="mr-1 action-badge"
                  @click="callbackSelected"
                >
                  {{ callbackLoading ? '處理中...' : '出席' }}
                </b-badge>

                <b-badge
                  variant="light-warning"
                  class="mr-1 action-badge"
                  @click="clearSelections"
                >
                  清除選擇
                </b-badge>

                <v-select
                  v-model="batchTeacherId"
                  class="teacher-select mr-50"
                  :options="staffOptions"
                  label="label"
                  :clearable="false"
                  :append-to-body="true"
                  :reduce="teacher => teacher.value"
                />

                <b-badge
                  variant="light-primary"
                  class="action-badge"
                  @click="change_enrollment('user_id', batchTeacherId, selectedEnrollmentIds)"
                >
                  轉老師
                </b-badge>
              </template>
            </b-col>

            <b-col cols="12" md="1" class="text-right">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                class="btn-icon"
                variant="flat-primary"
                @click="timetable_print()"
              >
                <feather-icon icon="PrinterIcon" />
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Timetable -->
        <div class="timetable-scroll">
          <div class="timetable-page">
            <div class="timetable-titlebar">
              <div>
                <div class="timetable-title">
                  {{ displayDate }}
                </div>
                <div class="timetable-subtitle">
                  日課程時間表（{{ items.length }} 位學生 / {{ teacherGroups.length }} 位老師）
                </div>
              </div>
            </div>

            <div class="schedule">
              <!-- Header -->
              <div
                class="time-header"
                :style="timeHeaderStyle"
              >
                <div class="time-label" />

                <div
                  v-for="hour in timelineHours"
                  :key="'top-' + hour"
                  class="hour"
                  :class="{ 'gray-hour': hour % 2 === 1 }"
                >
                  <strong>{{ padHour(hour) }}:00</strong>
                  <div class="quarter">
                    <span>00</span>
                    <span>15</span>
                    <span>30</span>
                    <span>45</span>
                  </div>
                </div>
              </div>

              <div v-if="teacherGroups.length === 0" class="no-data">
                當日沒有課堂資料
              </div>

              <!-- Teacher sections -->
              <section
                v-for="(teacher, teacherIndex) in teacherGroups"
                :key="teacher.key"
                class="teacher-block"
              >
                <div
                  class="teacher-name"
                  :class="teacherTheme(teacherIndex).name"
                >
                  {{ teacher.display_name }}
                  <small v-if="teacher.teacher_id">
                    ID: {{ teacher.teacher_id }}
                  </small>
                </div>

                <div
                  class="teacher-grid"
                  :style="teacherGridStyle(teacher)"
                >
                  <div
                    v-for="slot in teacher.slots"
                    :key="slot.enrollment_id"
                    class="slot"
                    :class="[
                      teacherTheme(teacherIndex).slot,
                      slotStatusClasses(slot),
                      { selected: isSelected(slot.enrollment_id) }
                    ]"
                    :style="slotGridStyle(slot)"
                    :title="slotTitle(slot)"
                    @click="toggleSelect(slot.enrollment_id)"
                    @dblclick.stop="openChangeEnrollment(slot)"
                  >
                    <div>
                      <div class="slot-mainline">
                        <span class="student-name">
                          {{ studentDisplayName(slot) }}
                        </span>

                        <span
                          v-if="ageShort(slot.birth)"
                          class="student-age"
                        >
                          {{ ageShort(slot.birth) }}
                        </span>

                        <span
                          v-if="slotGradeName(slot)"
                          class="class-grade"
                          :style="gradeStyle(slot)"
                        >
                          {{ slotGradeName(slot) }}
                        </span>
                      </div>

                      <span
                        v-if="!slot._has_valid_time"
                        class="remarks"
                      >
                        未設定時間
                      </span>

                      <span
                        v-if="slot.timetable_remarks"
                        class="remarks"
                      >
                        {{ slot.timetable_remarks }}
                      </span>

                      <span
                        v-if="slot.student_remarks"
                        class="remarks"
                      >
                        {{ slot.student_remarks }}
                      </span>

                      <span
                        v-if="slot.todo_content"
                        class="todo"
                        @dblclick.stop="change_todo(slot)"
                      >
                        {{ slot.todo_content }}
                      </span>

                      <span
                        v-if="slot.change_from > 0 || slot.change_from_class_date"
                        class="change-from-text"
                      >
                        {{ slot.change_from_class_date }}
                      </span>

                      <span
                        v-if="slot.enrollment_status === 'NONPAID'"
                        class="nonpaid"
                      >
                        未繳費
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Footer -->
              <div
                class="time-header time-footer"
                :style="timeHeaderStyle"
              >
                <div class="time-label" />

                <div
                  v-for="hour in timelineHours"
                  :key="'bottom-' + hour"
                  class="hour"
                  :class="{ 'gray-hour': hour % 2 === 1 }"
                >
                  <div class="quarter">
                    <span>00</span>
                    <span>15</span>
                    <span>30</span>
                    <span>45</span>
                  </div>
                  <strong>{{ padHour(hour) }}:00</strong>
                </div>
              </div>
            </div>

            <div class="timetable-help">
              單擊課堂：選取 / 取消選取　｜　雙擊課堂：轉堂
            </div>
          </div>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import flatPickr from 'vue-flatpickr-component'
import moment from 'moment'
import ChangeEnrollmentModal from '../member/member-edit/ChangeEnrollmentModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  BCard,
  BOverlay,
  BBadge,
  BRow,
  BCol,
  BFormInput,
  BFormCheckbox,
  BButton,
  BModal,
  VBModal,
} from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BOverlay,
    BBadge,
    BRow,
    BCol,
    BFormInput,
    BFormCheckbox,
    BButton,
    BModal,
    VBModal,
    vSelect,
    flatPickr,
    ChangeEnrollmentModal,
    ToastificationContent,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      rangeDate: moment().format('YYYY-MM-DD'),
      batchTeacherId: null,
      selected_enrollment: {
        class_date: null,
        class_hour: null,
        class_minute: null,
      },

      loading: false,
      selected_start: null,
      center_id: this.$store.getters['center/currentCenterId'],
      staffOptions: [],
      gradeOptions: [],
      staff_id: null,

      items: [],
      selectedEnrollmentIds: [],
      callbackLoading: false,
      scheduleStaffOptions: [],
      scheduleStartOptions: [],

      themes: [
        { name: 'blue-name', slot: 'blue' },
        { name: 'green-name', slot: 'green' },
        { name: 'orange-name', slot: 'orange' },
        { name: 'purple-name', slot: 'purple' },
        { name: 'cyan-name', slot: 'cyan' },
        { name: 'pink-name', slot: 'pink' },
      ],
    }
  },

  computed: {
    displayDate() {
      return moment(this.rangeDate, 'YYYY-MM-DD').format('YYYY MMM DD ddd')
    },

    validTimedItems() {
      return this.items.filter(item => this.isValidTimeRange(item.start, item.end))
    },

    timelineStartHour() {
      if (!this.validTimedItems.length) return 9

      const earliest = Math.min(
        ...this.validTimedItems.map(item => this.timeToMinutes(item.start)),
      )

      return Math.floor(earliest / 60)
    },

    timelineEndHour() {
      if (!this.validTimedItems.length) return 20

      const latest = Math.max(
        ...this.validTimedItems.map(item => this.timeToMinutes(item.end)),
      )

      const end = Math.ceil(latest / 60)
      return end <= this.timelineStartHour ? this.timelineStartHour + 1 : end
    },

    timelineHourCount() {
      return Math.max(1, this.timelineEndHour - this.timelineStartHour)
    },

    timelineGridColumns() {
      return this.timelineHourCount * 4
    },

    timelineHours() {
      const result = []

      for (let hour = this.timelineStartHour; hour < this.timelineEndHour; hour += 1) {
        result.push(hour)
      }

      return result
    },

    timeHeaderStyle() {
      return {
        gridTemplateColumns: `120px repeat(${this.timelineHourCount}, minmax(90px, 1fr))`,
      }
    },

    teacherGroups() {
      const groups = {}

      const sortedItems = [...this.items].sort((a, b) => {
        const teacherA = this.teacherName(a.user_id, a)
        const teacherB = this.teacherName(b.user_id, b)

        if (teacherA !== teacherB) {
          return teacherA.localeCompare(teacherB, 'zh-Hant')
        }

        return this.timeToMinutes(a.start) - this.timeToMinutes(b.start)
      })

      sortedItems.forEach(item => {
        const teacherId = item.user_id || item.teacher_id || null
        const key = teacherId || 'NO_TEACHER'

        if (!groups[key]) {
          groups[key] = {
            key,
            teacher_id: teacherId,
            display_name: this.teacherName(teacherId, item),
            rows: [],
            slots: [],
          }
        }

        const hasValidTime = this.isValidTimeRange(item.start, item.end)
        const startMin = hasValidTime
          ? this.timeToMinutes(item.start)
          : this.timelineStartHour * 60

        const endMin = hasValidTime
          ? this.timeToMinutes(item.end)
          : startMin + 15

        let rowNo = 1

        if (!hasValidTime) {
          rowNo = groups[key].rows.length + 1
          groups[key].rows[rowNo - 1] = []
        } else {
          while (true) {
            const rowIndex = rowNo - 1

            if (!groups[key].rows[rowIndex]) {
              groups[key].rows[rowIndex] = []
              break
            }

            const conflict = groups[key].rows[rowIndex].some(existing => (
              startMin < existing._end_min
              && endMin > existing._start_min
            ))

            if (!conflict) break
            rowNo += 1
          }
        }

        const slot = {
          ...item,
          _start_min: startMin,
          _end_min: endMin,
          _has_valid_time: hasValidTime,
          _row_no: rowNo,
        }

        groups[key].rows[rowNo - 1].push(slot)
        groups[key].slots.push(slot)
      })

      return Object.values(groups)
    },
  },

  watch: {
    rangeDate() {
      this.clearSelections()
      this.get_data()
    },
  },

  created() {
    this.get_data()
    this.get_staff_list()
    this.get_grade()
  },

  methods: {
    timeToMinutes(time) {
      if (!time || typeof time !== 'string') return 0

      const match = time.match(/^([01]\d|2[0-3]):([0-5]\d)/)
      if (!match) return 0

      return (parseInt(match[1], 10) * 60) + parseInt(match[2], 10)
    },

    isValidTimeRange(start, end) {
      if (!start || !end) return false

      const validStart = /^([01]\d|2[0-3]):[0-5]\d/.test(String(start))
      const validEnd = /^([01]\d|2[0-3]):[0-5]\d/.test(String(end))

      return validStart
        && validEnd
        && this.timeToMinutes(end) > this.timeToMinutes(start)
    },

    timeToGridColumn(time) {
      const minutes = this.timeToMinutes(time)
      const start = this.timelineStartHour * 60
      const offset = minutes - start

      let column = Math.round(offset / 15) + 1
      column = Math.max(1, column)
      column = Math.min(this.timelineGridColumns + 1, column)

      return column
    },

    teacherGridStyle(teacher) {
      const quarterWidth = 100 / this.timelineGridColumns
      const hourWidth = 100 / this.timelineHourCount
      const twoHourWidth = hourWidth * 2

      const firstBg = this.timelineStartHour % 2 === 1 ? '#efefef' : 'transparent'
      const secondBg = this.timelineStartHour % 2 === 1 ? 'transparent' : '#efefef'

      return {
        gridTemplateColumns: `repeat(${this.timelineGridColumns}, minmax(22px, 1fr))`,
        gridTemplateRows: `repeat(${Math.max(1, teacher.rows.length)}, minmax(42px, auto))`,
        '--quarter-width': `${quarterWidth}%`,
        '--hour-width': `${hourWidth}%`,
        '--two-hour-width': `${twoHourWidth}%`,
        '--first-hour-bg': firstBg,
        '--second-hour-bg': secondBg,
      }
    },

    slotGridStyle(slot) {
      let start = 1
      let end = 2

      if (slot._has_valid_time) {
        start = this.timeToGridColumn(slot.start)
        end = this.timeToGridColumn(slot.end)

        if (end <= start) end = start + 1
      }

      return {
        gridColumn: `${start} / ${end}`,
        gridRow: slot._row_no,
      }
    },

    teacherTheme(index) {
      return this.themes[index % this.themes.length]
    },

    teacherName(teacherId, item = {}) {
      if (item.display_name) return item.display_name
      if (item.teacher_name) return item.teacher_name
      if (item.user_name) return item.user_name

      const option = this.scheduleStaffOptions.find(
        row => String(row.value) === String(teacherId),
      ) || this.staffOptions.find(
        row => String(row.value) === String(teacherId),
      )

      return option ? option.label : (teacherId ? `Teacher #${teacherId}` : '未有老師')
    },

    studentDisplayName(slot) {
      return slot.student_name
        || slot.zh_name
        || slot.student_zh_name
        || `Student #${slot.student_id}`
    },

    slotGradeName(slot) {
      return slot.grade
        || slot.grade_name
        || slot.class_grade
        || ''
    },

    gradeStyle(slot) {
      let color = slot.grade_color || slot.color || ''

      if (!color) {
        return {}
      }

      color = String(color).trim()

      if (!color.startsWith('#') && !color.startsWith('rgb')) {
        color = `#${color}`
      }

      return {
        backgroundColor: color,
      }
    },

    slotStatusClasses(slot) {
      return {
        leave: slot.enrollment_status === 'LEAVE',
        trial: slot.enrollment_type === 'TRIAL',
        'short-course': slot.enrollment_type === 'SHORT_COURSE',
        changed: Number(slot.change_from) > 0 || !!slot.change_from_class_date,
      }
    },

    slotTitle(slot) {
      return [
        this.studentDisplayName(slot),
        `${slot.start || '--:--'} - ${slot.end || '--:--'}`,
        slot.class_id ? `Class #${slot.class_id}` : '',
      ].filter(Boolean).join('\n')
    },

    ageShort(dob) {
      if (!dob || !moment(dob, 'YYYY-MM-DD', true).isValid()) return ''

      const birth = moment(dob, 'YYYY-MM-DD')
      const ref = moment(this.rangeDate, 'YYYY-MM-DD')

      if (ref.isBefore(birth)) return ''

      const years = ref.diff(birth, 'years')
      const afterYears = birth.clone().add(years, 'years')
      const months = ref.diff(afterYears, 'months')

      return `${years}.${months}yr`
    },

    padHour(hour) {
      return String(hour).padStart(2, '0')
    },

    isSelected(enrollmentId) {
      return this.selectedEnrollmentIds.includes(enrollmentId)
    },

    get_grade() {
      this.$http.get(`${process.env.VUE_APP_API_BASE}/grades/`)
        .then(res => {
          this.gradeOptions = res.data.grades_enable
        })
    },

    submit_change_enrollment_grade(action) {
      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/${this.selected_enrollment.enrollment_id}/change_grade/?action=${action}`,
        {
          grade_id: this.selected_enrollment.grade_id,
          action,
        },
      )
        .then(() => {
          this.$refs['change-enrollment-grade-modal'].hide()
          this.successToast('調整成功')
          this.get_data()
        })
    },

    change_leave(enrollment) {
      const action = enrollment.enrollment_status === 'LEAVE'
        ? 'rollback'
        : 'absent'

      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/${enrollment.enrollment_id}/absent/?action=${action}`,
        {},
      )
        .then(() => {
          this.successToast('請假狀態已更新')
          this.get_data()
        })
    },

    change_todo(enrollment) {
      this.selected_enrollment = enrollment
      this.$refs['change-todo-modal'].show()
    },

    openChangeEnrollment(enrollment) {
      this.selected_enrollment = enrollment
      this.$nextTick(() => {
        this.$refs.changeEnrollmentModal.open(enrollment)
      })
    },

    change_enrollment_grade(enrollment) {
      this.selected_enrollment = enrollment
      this.$refs['change-enrollment-grade-modal'].show()
    },

    change_todo_content() {
      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/${this.selected_enrollment.enrollment_id}/update_todo/`,
        {
          todo_content: this.selected_enrollment.todo_content,
        },
      )
        .then(() => {
          this.successToast('修改畫題成功')
          this.$refs['change-todo-modal'].hide()
          this.get_data()
        })
    },

    timetable_print() {
      const date = moment(this.rangeDate).format('YYYY-MM-DD')
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')

      window.open(
        `//rb.cloudedu.pro/timetable-v3.php?token=${userData.token || ''}&date=${date}&center_id=${this.$store.getters['center/currentCenterId']}`,
        '_blank',
      )
    },

    toggleSelect(enrollmentId) {
      const idx = this.selectedEnrollmentIds.indexOf(enrollmentId)

      if (idx === -1) {
        this.selectedEnrollmentIds.push(enrollmentId)
      } else {
        this.selectedEnrollmentIds.splice(idx, 1)
      }
    },

    clearSelections() {
      this.selectedEnrollmentIds = []
    },

    selectAll() {
      this.selectedEnrollmentIds = this.items.map(item => item.enrollment_id)
    },

    callbackSelected() {
      if (!this.selectedEnrollmentIds.length || this.callbackLoading) return

      this.callbackLoading = true

      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/batch/change_enrollment/`,
        {
          enrollment_ids: this.selectedEnrollmentIds,
          field: 'is_attend',
          value: 'Y',
        },
      )
        .then(() => {
          this.successToast('操作成功')
          this.selectedEnrollmentIds = []
          this.get_data()
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: '操作失敗',
              icon: 'XIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.callbackLoading = false
        })
    },

    change_enrollment(field, value, enrollmentIds) {
      this.$http.post(
        `${process.env.VUE_APP_API_BASE}/enrollment/batch/change_enrollment/`,
        {
          field,
          value,
          enrollment_ids: enrollmentIds,
        },
      )
        .then(() => {
          this.get_data()
        })
    },

    get_staff_list() {
      this.$http.get(
        `${process.env.VUE_APP_API_BASE}/staffs/select/?center_id=${this.center_id}`,
      )
        .then(res => {
          this.staffOptions = res.data
        })
    },

    get_data() {
      this.loading = true

      this.$http.get(
        `${process.env.VUE_APP_API_BASE}/report/daily-schedule/?start=${this.selected_start}&center=${this.center_id}&date=${this.rangeDate}&staff_id=${this.staff_id}`,
      )
        .then(res => {
          this.items = res.data.enrollments || []
          this.scheduleStaffOptions = res.data.staff || []
          this.scheduleStartOptions = res.data.start || []
        })
        .finally(() => {
          this.loading = false
        })
    },

    successToast(title) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.teacher-select {
  min-width: 150px;
}

body .vs__dropdown-menu {
  z-index: 9999 !important;
}

body .vs__dropdown-toggle {
  background-color: #fff;
}

.action-badge {
  cursor: pointer;
  user-select: none;
}

.timetable-scroll {
  width: 100%;
  overflow-x: auto;
  border-top: 1px solid #ebe9f1;
}

.timetable-page {
  min-width: 1180px;
  padding: 18px 20px 22px;
  color: #333;
  background: #fff;
}

.timetable-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 12px;
}

.timetable-title {
  font-size: 25px;
  font-weight: 700;
  letter-spacing: 1px;
}

.timetable-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #777;
}

.schedule {
  border-top: 2px solid #777;
}

.time-header {
  display: grid;
  align-items: stretch;
  border-bottom: 1px solid #cfd4d8;
  background: #fff;
}

.time-label,
.hour {
  border-right: 1px solid #dfe3e7;
  padding: 8px 4px 6px;
  text-align: center;
}

.gray-hour {
  background: #efefef;
}

.hour strong {
  display: block;
  margin-bottom: 7px;
  font-size: 15px;
}

.quarter {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  font-size: 11px;
  color: #555;
}

.time-footer .quarter {
  margin-bottom: 7px;
}

.time-footer .hour strong {
  margin-bottom: 0;
}

.teacher-block {
  display: grid;
  grid-template-columns: 120px 1fr;
  border-bottom: 1px solid #cfd4d8;
}

.teacher-name {
  display: flex;
  min-height: 80px;
  padding: 8px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #d6dadd;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
}

.teacher-name small {
  margin-top: 5px;
  font-size: 11px;
  font-weight: 600;
}

.blue-name {
  color: #2d6fa6;
  background: #f1f8fd;
}

.green-name {
  color: #4b8c3f;
  background: #f3f9ee;
}

.orange-name {
  color: #d36b1e;
  background: #fff7ef;
}

.purple-name {
  color: #6536a4;
  background: #faf5ff;
}

.cyan-name {
  color: #2894a0;
  background: #effbfc;
}

.pink-name {
  color: #c72c63;
  background: #fff3f7;
}

.teacher-grid {
  position: relative;
  display: grid;
  gap: 4px 0;
  padding: 8px 0;
  background: none;
}

.teacher-grid::before {
  position: absolute;
  inset: 0;
  content: "";
  pointer-events: none;

  background:
    repeating-linear-gradient(
      to right,
      transparent 0,
      transparent calc(var(--quarter-width) - 1px),
      #ddd calc(var(--quarter-width) - 1px),
      #ddd var(--quarter-width)
    ),
    repeating-linear-gradient(
      to right,
      var(--first-hour-bg) 0,
      var(--first-hour-bg) var(--hour-width),
      var(--second-hour-bg) var(--hour-width),
      var(--second-hour-bg) var(--two-hour-width)
    );
}

.slot {
  z-index: 2;
  display: flex;
  min-height: 42px;
  height: auto;
  padding: 5px 6px;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  border-radius: 5px;
  overflow: visible;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  font-size: 12px;
  line-height: 1.25;
  text-align: center;
  cursor: pointer;
  transition: box-shadow .15s ease, transform .15s ease;
}

.slot:hover {
  z-index: 4;
  box-shadow: 0 3px 10px rgba(0, 0, 0, .12);
}

.slot.selected {
  box-shadow: inset 0 0 0 3px #7367f0;
}

.slot > div {
  width: 100%;
  min-width: 0;
}

.slot.blue,
.slot.green,
.slot.orange,
.slot.purple,
.slot.cyan,
.slot.pink {
  background: #fef5ee;
}

.slot.blue {
  border-color: #bed5e8;
}

.slot.green {
  border-color: #cfdfbd;
}

.slot.orange {
  border-color: #efd4b8;
}

.slot.purple {
  border-color: #d8c8e9;
}

.slot.cyan {
  border-color: #c4e4e5;
}

.slot.pink {
  border-color: #ecc9d6;
}

.slot.trial {
  background: #fdf5a0;
  border: 2px solid #ecad32;
}

.slot.short-course {
  background: #fef5ee;
  border: 2px solid #c8a3f2;
}

.slot.leave {
  color: red;
  font-weight: 700;
}

.slot.changed {
  box-shadow: inset 0 0 0 2px #198754;
}

.slot.changed.selected {
  box-shadow:
    inset 0 0 0 2px #198754,
    inset 0 0 0 5px #7367f0;
}

.slot-mainline {
  line-height: 1.5;
}

.student-name {
  font-weight: 700;
}

.student-age {
  margin-left: 4px;
  font-size: 10px;
  font-weight: 600;
}

.class-grade {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 12px;
  border-radius: 5px;
  background: #000;
  color: #444;
  font-size: 11px;
  font-weight: 700;
}

.remarks {
  display: block;
  margin-top: 2px;
  color: #b02a37;
  font-size: 9px;
}

.todo {
  display: block;
  margin-top: 2px;
  color: #666;
  font-size: 11px;
}

.slot.leave .todo {
  color: red;
  font-weight: 700;
}

.change-from-text {
  display: block;
  margin-top: 3px;
  color: #198754;
  font-size: 9px;
  font-weight: 700;
}

.nonpaid {
  display: block;
  margin-top: 3px;
  color: #ea5455;
  font-size: 10px;
  font-weight: 700;
}

.no-data {
  padding: 50px;
  color: #999;
  font-size: 20px;
  text-align: center;
}

.timetable-help {
  margin-top: 10px;
  color: #999;
  font-size: 11px;
}

@media print {
  .timetable-page {
    min-width: 0;
  }

  @page {
    size: A3 landscape;
    margin: 8mm;
  }
}
</style>
